import { useMutation } from '@apollo/client';
import { useI18n } from '@i18n';
import { useSnackbar } from 'notistack';
import { useCallback, useRef, useState } from 'react';
import { SnackbarTimeouts } from '../../../components/NotistackProvider';
import { DUPLICATE_SCENARIO } from '../../../graphql/ActionPlanning.mutations';
import { useSubscriptionDuplicateScenario } from '../subscriptions/useSubscriptionDuplicateScenario';

const useScenarioDuplication = () => {
  const { t } = useI18n();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [duplicatingScenarioIds, setDuplicatingScenarioIds] = useState<number[]>([]);
  const duplicationSnackbarKeysRef = useRef<Record<number, import('notistack').SnackbarKey>>({});
  const scenarioNamesRef = useRef<Record<number, string>>({});
  const [duplicateScenarioMutation] = useMutation(DUPLICATE_SCENARIO);

  const addDuplicatingScenarioId = useCallback((scenarioId: number) => {
    setDuplicatingScenarioIds((prev) => (prev.includes(scenarioId) ? prev : [...prev, scenarioId]));
  }, []);

  const closeScenarioDuplicationSnackbar = useCallback(
    (id: number) => {
      const key = duplicationSnackbarKeysRef.current[id];
      if (key !== undefined) {
        closeSnackbar(key);
        delete duplicationSnackbarKeysRef.current[id];
      }
    },
    [closeSnackbar],
  );

  const removeDuplicatingScenarioId = useCallback(
    (scenarioId: number) => {
      closeScenarioDuplicationSnackbar(scenarioId);
      delete scenarioNamesRef.current[scenarioId];
      setDuplicatingScenarioIds((prev) => prev.filter((it) => it !== scenarioId));
    },
    [closeScenarioDuplicationSnackbar],
  );

  const duplicateScenario = (scenarioId: number, scenarioName: string) => {
    addDuplicatingScenarioId(scenarioId);
    scenarioNamesRef.current[scenarioId] = scenarioName;

    duplicateScenarioMutation({
      mutation: DUPLICATE_SCENARIO,
      variables: {
        scenarioId,
        copyNameSuffix: t('ActionPlanning_Scenario_DuplicateScenarioDialog_ScenarioCopySuffix'),
      },
      refetchQueries: ['ActionPlanningScenariosAllScenarios'],
      awaitRefetchQueries: false,
    });

    const key = enqueueSnackbar(
      t('ActionPlanning_Scenario_DuplicateScenarioDialog_DuplicationInProgress', { scenarioName: scenarioName }),
      {
        variant: 'success',
        autoHideDuration: SnackbarTimeouts.Success,
      },
    );

    duplicationSnackbarKeysRef.current[scenarioId] = key;
  };

  // We do this here to avoid a scenario where the subscription is never mounted and refetchScenarios is never executed, so the list remains stale.
  const DuplicationWatcher = ({ id }: { id: number }) => {
    const scenarioName = scenarioNamesRef.current[id] ?? '';
    useSubscriptionDuplicateScenario({
      scenarioId: id,
      scenarioName: scenarioName,
      isWatched: true,
      onFinish: () => removeDuplicatingScenarioId(id),
    });

    return null;
  };

  return {
    duplicatingScenarioIds,
    addDuplicatingScenarioId,
    removeDuplicatingScenarioId,
    duplicateScenario,
    closeScenarioDuplicationSnackbar,
    DuplicationWatcher,
  };
};

export default useScenarioDuplication;
